import { Helmet } from 'react-helmet-async';
import { useState, useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useTheme } from '@mui/material/styles';
import { Box, Button, IconButton, MenuItem, Popover, Stack, Tooltip } from '@mui/material';
import { DataGrid, Toolbar } from '@mui/x-data-grid';

import Iconify from '../../components/iconify';
import Label from '../../components/label';
import { ToolBarSearch, ToolBarColumnToggler, Container as DataGridContainer } from '../../components/datagrid';
import { fCurrency } from '../../utils/formatNumber';
import { useGetTestsQuery, useDelTestMutation } from '../../slices/apis/testApi';
import type { TestProps } from '../../models';
import { useRole } from '../../hooks/useRole';

type DataGridRow = {
	id: TestProps['id'];
	name: TestProps['name'];
	desc: TestProps['desc'];
	price: TestProps['price'];
	index: TestProps['index'];
	isBulk: TestProps['isBulk'];
};

export default function Tests() {
	const { isAdmin } = useRole();
	const [menuOpen, setMenuOpen] = useState<HTMLButtonElement | null>(null);
	const [selectedTestId, setSelectedTestId] = useState<string | number | null>(null);

	const navigate = useNavigate();
	const theme = useTheme();
	const { data = [], isFetching } = useGetTestsQuery();
	const [delTestMutation] = useDelTestMutation();

	const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
		setMenuOpen(event.currentTarget);
		setSelectedTestId(event.currentTarget.getAttribute('data-test-id'));
	};

	const handleCloseMenu = () => {
		setMenuOpen(null);
	};

	const handleDeleteTest = () => {
		delTestMutation(selectedTestId);
		setMenuOpen(null);
	};

	const handleEditTest = () => {
		navigate(`/dashboard/admin/tests/${selectedTestId}/edit`);
		setMenuOpen(null);
	};

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	return (
		<>
			<Helmet>
				<title>Tests - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Box
				sx={{
					width: { xs: '100%', md: '85%' },
					ml: { xs: 0, md: 3, lg: 4 },
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Button
						variant="contained"
						data-route="/dashboard/admin/tests/new"
						onClick={handleNavigate}
						startIcon={<Iconify icon="eva:plus-fill" />}
					>
						New Test
					</Button>
				</Stack>

				<DataGridContainer>
					<DataGrid
						disableColumnFilter
						disableDensitySelector
						disableColumnSelector
						showToolbar
						loading={isFetching}
						columnHeaderHeight={48}
						initialState={{
							sorting: {
								sortModel: [{ field: 'index', sort: 'asc' }],
							},
						}}
						slots={{
							toolbar: () => (
								<Toolbar>
									<Box sx={{ pl: 1 }}>
										<ToolBarSearch />
									</Box>
									<Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
										{isAdmin && (
											<Tooltip title="Reorder">
												<IconButton
													color="secondary"
													data-route="/dashboard/admin/tests/reorder"
													onClick={handleNavigate}
												>
													<SwapVertIcon />
												</IconButton>
											</Tooltip>
										)}
										<Tooltip title="Columns">
											<ToolBarColumnToggler />
										</Tooltip>
									</Box>
								</Toolbar>
							),
						}}
						sx={{
							width: '100%',
							borderRadius: 3,
							'& .MuiDataGrid-columnHeaderTitle': {
								whiteSpace: 'normal',
								lineHeight: 1.4,
								fontSize: '0.9rem',
								color: 'grey',
							},
							'& .MuiDataGrid-columnHeader': {
								backgroundColor: theme.palette.grey[100],
							},
						}}
						columns={[
							{
								field: 'id',
								headerName: 'ID',
								resizable: false,
								sortable: false,
								flex: 0.25,
							},
							{
								field: 'name',
								headerName: 'Name',
								resizable: false,
								flex: 1,
							},
							{
								field: 'desc',
								headerName: 'Description',
								resizable: false,
								flex: 1.5,
							},
							{
								field: 'price',
								headerName: 'Price',
								resizable: false,
								flex: 0.5,
								renderCell: (params) => fCurrency(params.row.price),
							},
							{
								field: 'isBulk',
								headerName: 'Bulk',
								resizable: false,
								flex: 0.4,
								renderCell: (params) => {
									return params.row.isBulk ? <Label color="success">BULK</Label> : null;
								},
							},
							{
								field: 'index',
								headerName: 'Order',
								resizable: false,
								flex: 0.35,
							},
							{
								field: 'actions',
								headerName: '',
								headerAlign: 'center',
								align: 'right',
								flex: 0.3,
								sortable: false,
								filterable: false,
								resizable: false,
								renderCell: (params) => {
									const {
										row: { id },
									} = params;

									return (
										<IconButton size="large" color="inherit" data-test-id={id} onClick={handleOpenMenu}>
											<Iconify icon={'eva:more-vertical-fill'} />
										</IconButton>
									);
								},
							},
						]}
						rows={data.map((test) => {
							return {
								id: test.id,
								name: test.name,
								desc: test.desc,
								price: test.price,
								index: test.index,
								isBulk: test.isBulk,
								actions: '',
							} as DataGridRow;
						})}
					/>
				</DataGridContainer>
			</Box>

			<Popover
				open={Boolean(menuOpen)}
				anchorEl={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 1,
						width: 140,
						'& .MuiMenuItem-root': {
							px: 1,
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<MenuItem onClick={handleEditTest}>
					<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
					View/Edit
				</MenuItem>
				<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteTest}>
					<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
					Delete
				</MenuItem>
			</Popover>
		</>
	);
}
